import React from 'react'
import { BIG_0, FactoryVersion, JSBI, Pair, Percent } from '@nominex/nomiswap-sdk'
import { Link } from 'react-router-dom'
import { SlDetails, SlButton, SlIcon } from '@shoelace-style/shoelace/dist/react'
import { CardProps, Text } from '@nominex/dex-uikit'
import tw from 'twin.macro'
import styled from 'styled-components'

import { CurrencyImage, CurrencyPairImage } from 'components/CurrencyImage'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import useTotalSupply from 'hooks/useTotalSupply'
import { useTokenBalance } from 'state/wallet/hooks'
import { currencyId } from 'utils/currency'
import { formatNumber } from 'utils/formatters'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { ConcentratedLiquidityBadge } from 'views/Farms/components/FarmTable/Badges'

import { LightCard } from '../Card'
import { AutoColumn, RowBetween, RowFixed } from '../Layout'

const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`

const StyledSlDetails = styled(SlDetails)<{ nonClosable?: boolean }>`
  &::part(base) {
    ${tw`bg-dark-gray-600 text-white border-none rounded-12`}
  }

  &::part(header) {
    ${({ nonClosable }) => nonClosable && tw`cursor-default pointer-events-none`};
  }

  &::part(summary-icon) {
    ${({ nonClosable }) => nonClosable && tw`hidden`};
  }
`

interface PositionCardProps extends CardProps {
  pair: Pair
  showUnwrapped?: boolean
}

export function MinimalPositionCard({ pair, showUnwrapped = false }: PositionCardProps) {
  const { account } = useWagmi()

  const { t } = useTranslation()

  const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
  const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? new Percent(userPoolBalance.quotient, totalPoolTokens.quotient)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return userPoolBalance && JSBI.greaterThan(userPoolBalance.quotient, JSBI.BigInt(0)) ? (
    <div>
      <h3 className="text-16 text-white font-medium mb-16">{t('LP tokens on your wallet')}</h3>
      <StyledSlDetails open nonClosable>
        <div className="flex items-center justify-between" slot="summary">
          <div className="flex items-center space-x-8">
            <CurrencyPairImage currencyA={currency0} currencyB={currency1} size="24px" />
            <span className="font-medium notranslate" translate="no">{`${currency0.symbol}/${currency1.symbol}`}</span>
            {pair.factoryVersion === FactoryVersion.V2 && (
              <div className="flex items-center">
                <ConcentratedLiquidityBadge />
              </div>
            )}
          </div>
        </div>

        <AutoColumn gap="8px">
          <FixedHeightRow>
            <RowFixed>
              <CurrencyImage currency={currency0} size="20px" />
              <Text color="darkGray100" ml="4px">
                {t('Pooled')}{' '}
                <Text display="inline" color="darkGray100" noTranslate>
                  {currency0.symbol}
                </Text>
              </Text>
            </RowFixed>
            {token0Deposited ? (
              <RowFixed>
                <Text fontSize="16px" color="white" ml="6px" bold>
                  {formatNumber(Number(token0Deposited.toExact()))}
                </Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <RowFixed>
              <CurrencyImage currency={currency1} size="20px" />
              <Text color="darkGray100" ml="4px">
                {t('Pooled')}{' '}
                <Text display="inline" color="darkGray100" noTranslate>
                  {currency1.symbol}
                </Text>
              </Text>
            </RowFixed>
            {token1Deposited ? (
              <RowFixed>
                <Text fontSize="16px" color="white" ml="6px" bold>
                  {formatNumber(Number(token1Deposited.toExact()))}
                </Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <RowFixed>
              <Text color="darkGray100">{t('Pool tokens')}</Text>
            </RowFixed>
            {userPoolBalance ? (
              <RowFixed>
                <Text fontSize="16px" color="white" ml="6px" bold>
                  {formatNumber(Number(userPoolBalance.toFixed(8)))}
                </Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <Text color="darkGray100">Share of pool</Text>
            <Text fontSize="16px" color="white" ml="6px" bold>
              {poolTokenPercentage
                ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
                : '-'}
            </Text>
          </FixedHeightRow>
        </AutoColumn>
      </StyledSlDetails>
    </div>
  ) : (
    <LightCard>
      <Text fontSize="14px" style={{ textAlign: 'center' }}>
        {t(
          "By adding liquidity you'll earn 0.03% of all trades on this pair proportional to your share of the pool. Fees are added to the pool, accrue in real time and can be claimed by withdrawing your liquidity.",
        )}
      </Text>
    </LightCard>
  )
}

export default function FullPositionCard({ pair }: PositionCardProps) {
  const { account } = useWagmi()
  const { t } = useTranslation()

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? new Percent(userPoolBalance.quotient, totalPoolTokens.quotient)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return (
    <StyledSlDetails>
      <div className="flex items-center justify-between" slot="summary">
        <div className="flex items-center space-x-8">
          <CurrencyPairImage currencyA={currency0} currencyB={currency1} size="24px" />
          <span className="font-medium notranslate" translate="no">{`${currency0.symbol}/${currency1.symbol}`}</span>
          {pair.factoryVersion === FactoryVersion.V2 && (
            <div className="flex items-center">
              <ConcentratedLiquidityBadge />
            </div>
          )}
        </div>
      </div>

      <AutoColumn gap="8px">
        <FixedHeightRow>
          <RowFixed>
            <CurrencyImage currency={currency0} size="20px" />
            <Text color="darkGray100" ml="4px">
              {t('Pooled')}{' '}
              <Text display="inline" color="darkGray100" noTranslate>
                {currency0.symbol}
              </Text>
            </Text>
          </RowFixed>
          {token0Deposited ? (
            <RowFixed>
              <Text fontSize="16px" color="white" ml="6px" bold>
                {formatNumber(Number(token0Deposited.toExact()))}
              </Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>

        <FixedHeightRow>
          <RowFixed>
            <CurrencyImage currency={currency1} size="20px" />
            <Text color="darkGray100" ml="4px">
              {t('Pooled')}{' '}
              <Text display="inline" color="darkGray100" noTranslate>
                {currency1.symbol}
              </Text>
            </Text>
          </RowFixed>
          {token1Deposited ? (
            <RowFixed>
              <Text fontSize="16px" color="white" ml="6px" bold>
                {formatNumber(Number(token1Deposited.toExact()))}
              </Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>

        <FixedHeightRow>
          <RowFixed>
            <Text color="darkGray100">{t('Pool tokens')}</Text>
          </RowFixed>
          {userPoolBalance ? (
            <RowFixed>
              <Text fontSize="16px" color="white" ml="6px" bold>
                {formatNumber(Number(userPoolBalance.toExact()))}
              </Text>
            </RowFixed>
          ) : (
            '-'
          )}
        </FixedHeightRow>

        <FixedHeightRow>
          <Text color="darkGray100">Share of pool</Text>
          <Text fontSize="16px" color="white" ml="6px" bold>
            {poolTokenPercentage
              ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
              : '-'}
          </Text>
        </FixedHeightRow>

        {userPoolBalance && JSBI.greaterThan(userPoolBalance.quotient, BIG_0) && (
          <div className="flex flex-col gap-8 mt-16">
            <Link
              to={`/liquidity/remove/${currencyId(currency0)}/${currencyId(currency1)}${
                pair.factoryVersion !== FactoryVersion.V1 ? `?version=${pair.factoryVersion}` : ''
              }`}
            >
              <SlButton variant="neutral" className="w-[100%]">
                {t('Remove liquidity')}
              </SlButton>
            </Link>
            <Link
              to={`/liquidity/add/${currencyId(currency0)}/${currencyId(currency1)}${
                pair.factoryVersion !== FactoryVersion.V1 ? `?version=${pair.factoryVersion}` : ''
              }`}
            >
              <SlButton variant="text" className="w-[100%]">
                <SlIcon name="plus" library="unicons" slot="prefix" />
                {t('Add liquidity instead')}
              </SlButton>
            </Link>
          </div>
        )}
      </AutoColumn>
    </StyledSlDetails>
  )
}
